/**
 * Chart drawing utilities — calculations for Fibonacci, trend lines, etc.
 */

// Fibonacci retracement levels (standard)
export const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1.0];

// Fibonacci extension levels
export const FIB_EXTENSIONS = [1.272, 1.618, 2.0, 2.618];

/**
 * Calculate Fibonacci retracement levels between two price points
 * @param {number} p1 - Start price
 * @param {number} p2 - End price
 * @returns {Array} Array of {level, price} objects
 */
export function calcFibLevels(p1, p2) {
  const diff = p2 - p1;
  return FIB_LEVELS.map(level => ({
    level,
    price: p2 - diff * level,
  }));
}

/**
 * Calculate Fibonacci extension levels
 * @param {number} p1 - Start price (swing low/high)
 * @param {number} p2 - End price (swing high/low)
 * @returns {Array} Array of {level, price} objects
 */
export function calcFibExtensions(p1, p2) {
  const diff = p2 - p1;
  return FIB_EXTENSIONS.map(level => ({
    level,
    price: p2 + diff * (level - 1),
  }));
}

/**
 * Calculate linear regression line
 * @param {Array} points - Array of {x, y} objects
 * @returns {Object} {slope, intercept, rSquared}
 */
export function linearRegression(points) {
  const n = points.length;
  if (n < 2) return null;

  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0, sumYY = 0;
  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumXX += p.x * p.x;
    sumYY += p.y * p.y;
  }

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  // R-squared
  const yMean = sumY / n;
  const ssTot = sumYY - n * yMean * yMean;
  const ssRes = points.reduce((sum, p) => {
    const predicted = slope * p.x + intercept;
    return sum + (p.y - predicted) ** 2;
  }, 0);
  const rSquared = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return { slope, intercept, rSquared };
}

/**
 * Calculate moving average
 * @param {Array} values - Array of numbers
 * @param {number} period - MA period
 * @returns {Array} Array of {index, value} (null for insufficient data)
 */
export function calcMA(values, period) {
  const result = [];
  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) {
      result.push({ index: i, value: null });
    } else {
      const slice = values.slice(i - period + 1, i + 1);
      const avg = slice.reduce((a, b) => a + b, 0) / period;
      result.push({ index: i, value: avg });
    }
  }
  return result;
}

/**
 * Calculate exponential moving average
 * @param {Array} values - Array of numbers
 * @param {number} period - EMA period
 * @returns {Array} Array of {index, value}
 */
export function calcEMA(values, period) {
  const result = [];
  const multiplier = 2 / (period + 1);

  for (let i = 0; i < values.length; i++) {
    if (i === 0) {
      result.push({ index: i, value: values[0] });
    } else {
      const ema = (values[i] - result[i - 1].value) * multiplier + result[i - 1].value;
      result.push({ index: i, value: ema });
    }
  }
  return result;
}

/**
 * Calculate RSI (Relative Strength Index)
 * @param {Array} values - Array of prices
 * @param {number} period - RSI period (default 14)
 * @returns {Array} Array of {index, value}
 */
export function calcRSI(values, period = 14) {
  const result = [];
  const gains = [];
  const losses = [];

  for (let i = 1; i < values.length; i++) {
    const change = values[i] - values[i - 1];
    gains.push(change > 0 ? change : 0);
    losses.push(change < 0 ? -change : 0);
  }

  result.push({ index: 0, value: null });

  for (let i = 0; i < gains.length; i++) {
    if (i < period - 1) {
      result.push({ index: i + 1, value: null });
    } else if (i === period - 1) {
      const avgGain = gains.slice(0, period).reduce((a, b) => a + b, 0) / period;
      const avgLoss = losses.slice(0, period).reduce((a, b) => a + b, 0) / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsi = 100 - 100 / (1 + rs);
      result.push({ index: i + 1, value: rsi });
    } else {
      const prevRsi = result[result.length - 1].value;
      const prevAvgGain = gains.slice(i - period, i).reduce((a, b) => a + b, 0) / period;
      const prevAvgLoss = losses.slice(i - period, i).reduce((a, b) => a + b, 0) / period;
      const avgGain = (prevAvgGain * (period - 1) + gains[i]) / period;
      const avgLoss = (prevAvgLoss * (period - 1) + losses[i]) / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsi = 100 - 100 / (1 + rs);
      result.push({ index: i + 1, value: rsi });
    }
  }

  return result;
}

/**
 * Calculate MACD (Moving Average Convergence Divergence)
 * @param {Array} values - Array of prices
 * @param {number} fastPeriod - Fast EMA period (default 12)
 * @param {number} slowPeriod - Slow EMA period (default 26)
 * @param {number} signalPeriod - Signal line period (default 9)
 * @returns {Object} {macd, signal, histogram}
 */
export function calcMACD(values, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) {
  const fastEMA = calcEMA(values, fastPeriod);
  const slowEMA = calcEMA(values, slowPeriod);

  const macdLine = [];
  for (let i = 0; i < values.length; i++) {
    if (i < slowPeriod - 1) {
      macdLine.push({ index: i, value: null });
    } else {
      macdLine.push({ index: i, value: fastEMA[i].value - slowEMA[i].value });
    }
  }

  const macdValues = macdLine.filter(m => m.value !== null).map(m => m.value);
  const signalEMA = calcEMA(macdValues, signalPeriod);

  const signal = [];
  const histogram = [];

  for (let i = 0; i < values.length; i++) {
    if (i < slowPeriod + signalPeriod - 2) {
      signal.push({ index: i, value: null });
      histogram.push({ index: i, value: null });
    } else {
      const signalIdx = i - (slowPeriod - 1);
      const signalValue = signalEMA[signalIdx]?.value ?? null;
      signal.push({ index: i, value: signalValue });
      histogram.push({
        index: i,
        value: signalValue !== null && macdLine[i].value !== null
          ? macdLine[i].value - signalValue
          : null,
      });
    }
  }

  return { macd: macdLine, signal, histogram };
}

/**
 * Calculate Bollinger Bands
 * @param {Array} values - Array of prices
 * @param {number} period - MA period (default 20)
 * @param {number} stdDev - Standard deviation multiplier (default 2)
 * @returns {Object} {upper, middle, lower}
 */
export function calcBollinger(values, period = 20, stdDev = 2) {
  const middle = calcMA(values, period);
  const upper = [];
  const lower = [];

  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) {
      upper.push({ index: i, value: null });
      lower.push({ index: i, value: null });
    } else {
      const slice = values.slice(i - period + 1, i + 1);
      const mean = middle[i].value;
      const variance = slice.reduce((sum, v) => sum + (v - mean) ** 2, 0) / period;
      const std = Math.sqrt(variance);
      upper.push({ index: i, value: mean + stdDev * std });
      lower.push({ index: i, value: mean - stdDev * std });
    }
  }

  return { upper, middle, lower };
}

/**
 * Calculate ATR (Average True Range)
 * @param {Array} candles - Array of {high, low, close}
 * @param {number} period - ATR period (default 14)
 * @returns {Array} Array of {index, value}
 */
export function calcATR(candles, period = 14) {
  const trueRanges = [];

  for (let i = 0; i < candles.length; i++) {
    if (i === 0) {
      trueRanges.push(candles[i].high - candles[i].low);
    } else {
      const tr = Math.max(
        candles[i].high - candles[i].low,
        Math.abs(candles[i].high - candles[i - 1].close),
        Math.abs(candles[i].low - candles[i - 1].close)
      );
      trueRanges.push(tr);
    }
  }

  const result = [];
  for (let i = 0; i < trueRanges.length; i++) {
    if (i < period - 1) {
      result.push({ index: i, value: null });
    } else if (i === period - 1) {
      const atr = trueRanges.slice(0, period).reduce((a, b) => a + b, 0) / period;
      result.push({ index: i, value: atr });
    } else {
      const prevATR = result[i - 1].value;
      const atr = (prevATR * (period - 1) + trueRanges[i]) / period;
      result.push({ index: i, value: atr });
    }
  }

  return result;
}

/**
 * Check if price is inside a zone
 * @param {number} price - Current price
 * @param {Object} zone - Zone object with bot, top properties
 * @returns {boolean}
 */
export function isPriceInZone(price, zone) {
  return price >= zone.bot && price <= zone.top;
}

/**
 * Find nearest zone to current price
 * @param {number} price - Current price
 * @param {Array} zones - Array of zone objects
 * @returns {Object|null} Nearest zone with distance
 */
export function findNearestZone(price, zones) {
  if (!zones?.length) return null;

  let nearest = null;
  let minDistance = Infinity;

  for (const zone of zones) {
    const distance = price < zone.bot
      ? zone.bot - price
      : price > zone.top
        ? price - zone.top
        : 0;

    if (distance < minDistance) {
      minDistance = distance;
      nearest = { ...zone, distance };
    }
  }

  return nearest;
}
